import React, { useState } from 'react';
import { Calendar, MapPin, Clock, Check } from 'lucide-react';
import PageHero, { Section, InfoCard, CTABlock } from '../components/PageHero';
import { Button } from '../components/ui/button';
import { useToast } from '../hooks/use-toast';
import { submitInquiry } from '../lib/api';

const benefits = [
  '1-on-1 with a salaried counsellor — no commission bias',
  'On-the-spot NEET score & profile evaluation',
  'University shortlist tailored to your budget',
  'Meet university representatives directly',
  'Transparent fee structure walkthrough',
  'Visa & travel process explained end-to-end',
];

const cities = ['Delhi', 'Mumbai', 'Ahmedabad', 'Patna', 'Chennai', 'Bangalore', 'Kolkata', 'Jaipur', 'Hyderabad', 'Varanasi', 'Pune', 'Guwahati'];

export default function Seminar() {
  const { toast } = useToast();
  const [f, setF] = useState({ name: '', phone: '', email: '', city: '', neet: '' });
  const [submitting, setSubmitting] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!f.name || !f.phone) { toast({ title: 'Please fill Name & Phone', variant: 'destructive' }); return; }
    setSubmitting(true);
    try {
      await submitInquiry({
        type: 'seminar',
        name: f.name,
        phone: f.phone,
        email: f.email || undefined,
        city: f.city || undefined,
        neet: f.neet || undefined,
      });
      toast({ title: 'Seat Reserved!', description: `Thanks ${f.name} — we'll call you within 24 hours.` });
      setF({ name: '', phone: '', email: '', city: '', neet: '' });
    } catch (err) {
      toast({ title: 'Could not reserve seat', description: err.message, variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <PageHero eyebrow="Book Your Seat" title="Free MBBS in Russia" highlight="Guidance Seminar"
        subtitle="Attend our offline seminars in 12 Indian cities and get direct clarity on universities, fees, admission process and visa."
        breadcrumbs={[{ label: 'Seminar' }]} />

      <Section>
        <div className="grid lg:grid-cols-[1fr_400px] gap-10 items-start">
          <div>
            <h3 className="font-serif text-2xl md:text-3xl font-bold text-primary">What You Get at the Seminar</h3>
            <div className="mt-5 grid sm:grid-cols-2 gap-3">
              {benefits.map((b, i) => (
                <div key={i} className="flex items-start gap-2.5 p-3 rounded-xl bg-white border border-border">
                  <div className="w-6 h-6 rounded-full bg-brand text-white flex items-center justify-center shrink-0"><Check className="w-3.5 h-3.5" strokeWidth={3} /></div>
                  <span className="text-sm text-foreground">{b}</span>
                </div>
              ))}
            </div>
            <div className="mt-8">
              <h4 className="font-serif text-xl font-bold text-primary">Available in 12 Cities</h4>
              <div className="mt-3 flex flex-wrap gap-2">
                {cities.map((c) => (
                  <span key={c} className="px-3 py-1.5 rounded-full bg-brand/10 text-brand text-xs font-bold">✦ {c}</span>
                ))}
              </div>
            </div>
          </div>

          <form onSubmit={submit} className="bg-white rounded-2xl border border-border shadow-lg p-6">
            <div className="text-xs uppercase tracking-widest text-brand font-bold">Reserve Your Seat</div>
            <h4 className="font-serif text-xl font-bold text-primary mt-1">Free Entry</h4>
            <div className="mt-4 space-y-3">
              <input value={f.name} onChange={e=>setF({...f,name:e.target.value})} placeholder="Full Name *" className="w-full h-10 px-3 rounded-lg border border-border text-sm focus:outline-none focus:border-brand focus:ring-2 focus:ring-brand/20" />
              <input value={f.phone} onChange={e=>setF({...f,phone:e.target.value})} type="tel" placeholder="Phone Number *" className="w-full h-10 px-3 rounded-lg border border-border text-sm focus:outline-none focus:border-brand focus:ring-2 focus:ring-brand/20" />
              <input value={f.email} onChange={e=>setF({...f,email:e.target.value})} type="email" placeholder="Email" className="w-full h-10 px-3 rounded-lg border border-border text-sm focus:outline-none focus:border-brand focus:ring-2 focus:ring-brand/20" />
              <select value={f.city} onChange={e=>setF({...f,city:e.target.value})} className="w-full h-10 px-3 rounded-lg border border-border text-sm bg-white focus:outline-none focus:border-brand focus:ring-2 focus:ring-brand/20">
                <option value="">Preferred City</option>
                {cities.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
              <input value={f.neet} onChange={e=>setF({...f,neet:e.target.value})} placeholder="NEET Score (optional)" className="w-full h-10 px-3 rounded-lg border border-border text-sm focus:outline-none focus:border-brand focus:ring-2 focus:ring-brand/20" />
              <Button type="submit" className="w-full bg-brand hover:bg-brand-dark text-white h-11 rounded-lg font-semibold btn-shift">Register Now</Button>
            </div>
            <p className="mt-3 text-[10px] text-muted-foreground text-center">By registering, you agree to receive callback from Russian Education.</p>
          </form>
        </div>
      </Section>

      <CTABlock title="Can't attend in-person?" subtitle="Join our online video counselling from anywhere in India." cta="Book Online Session" href="/video-counselling" />
    </>
  );
}
